import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'

interface SimulateResponse {
  queued: number
  platforms: number
  results: number
  failures: { platform: string; error: string }[]
}

interface BulkAddResponse {
  added: number
}

export interface TrackAndRunAllInput {
  queryTexts: string[]
  platforms: string[]
}

// Distinguishes "nothing happened" (bulk-add itself failed) from "prompts
// were added but the run failed" — losing the tracking on a network blip
// is worse than leaving an un-run prompt, so callers must be able to tell
// the two apart and never imply the add was rolled back.
export class TrackAndRunAllError extends Error {
  stage: 'add' | 'run'
  addedCount: number
  cause: unknown

  constructor(stage: 'add' | 'run', addedCount: number, cause: unknown) {
    super(stage === 'add' ? 'Failed to add prompts' : 'Prompts added, but the run failed')
    this.stage = stage
    this.addedCount = addedCount
    this.cause = cause
  }
}

export function useTrackAndRunAll(websiteId: string | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ queryTexts, platforms }: TrackAndRunAllInput) => {
      try {
        await api.post<BulkAddResponse>('/api/prompts/bulk', {
          prompts: queryTexts,
          websiteId: websiteId ?? undefined,
        })
      } catch (error) {
        throw new TrackAndRunAllError('add', 0, error)
      }

      try {
        return await api.post<SimulateResponse>('/api/simulate', {
          queries: queryTexts.map((t) => ({ query_text: t })),
          platforms,
          website_id: websiteId,
        })
      } catch (error) {
        throw new TrackAndRunAllError('run', queryTexts.length, error)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['simulate-results'] })
      void queryClient.invalidateQueries({ queryKey: ['credits-balance'] })
      void queryClient.invalidateQueries({ queryKey: ['trending-prompts', websiteId] })
    },
  })
}
